'use client';

import { inter400, inter600 } from '@/config/fonts';
import classNames from 'classnames';

import SpotlightSvg from '@/assets/svgs/spotlight.svg';
import InfoAlert from '@/shared/components/InfoAlert/InfoAlert';
import { OptionalFeatureSchema } from '@/containers/ContactLists/types';
import {
  selectAccountIdentities,
  selectCurrentAccountId,
} from '@/lib/redux/app.selectors';
import { useFetchAccountDetails } from '@/containers/Accounts/api';
import { HorizontalSelectList, InputContainer } from '@/shared/components/FormInputs';
import { IdentitySelector } from '@/shared/components/IdentitySelector';
import { listTypeOptions } from '@/shared/utils/contact-list';
import { FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { P2PUploadParams } from '../types';
import { UploadContactListFormValues } from './P2PUploadModal';

interface P2PUploadStep1UploadFileProps {
  className?: string;
  params?: P2PUploadParams;
  formik: FormikProps<UploadContactListFormValues>;
  isLoading: boolean;
  validFileExtensions: string[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  shouldValidateIdentity: boolean;
  optionalFeatures?: OptionalFeatureSchema[];
}

const P2PUploadStep1UploadFile = ({
  className,
  params,
  formik,
  isLoading,
  validFileExtensions,
  fileInputRef,
  shouldValidateIdentity,
  optionalFeatures,
}: P2PUploadStep1UploadFileProps) => {
  const accountIdentities = useSelector(selectAccountIdentities);
  const currentAccountId = useSelector(selectCurrentAccountId);
  const { data: accountDetails } = useFetchAccountDetails(
    currentAccountId || undefined,
    !!currentAccountId
  );
  const hasRateplan = !!accountDetails?.data?.p2p_rateplan;

  return (
    <div className={classNames('', className)}>
      <div className="pb-4">
        <div className={classNames(inter600.className, 'text-2xl')}>
          {params?.type === 'suppression' && 'Upload a Suppression List'}
          {params?.type === 'p2p-sms' && 'Upload a Contact List'}
          {params?.type === 'test' && 'Upload a Test List'}
          {!params && 'Upload a Contact List'}
        </div>
        <div className={classNames('pt-2 text-sm text-gray-600 dark:text-gray-400')}>
          Contact Lists contain the phone numbers and data for the people you would like
          to send your P2P messages to. Here you can upload Contact Lists to your
          Identity.
        </div>
      </div>

      <InfoAlert>
        There will be a list scrubbing charge of $0.0025 per Contact Loaded, billed the
        first time you assign this list to a job. Invalid, Malformed and Duplicated
        records will not be billed.
      </InfoAlert>

      {/* Form: START */}
      <div
        className={classNames('my-4 flex flex-col gap-4', {
          'disabled-container': isLoading,
        })}>
        <InputContainer
          label="Upload Your Contact List"
          error={formik.errors.file}
          isTouched={formik.touched.file}>
          <input
            type="file"
            name="file"
            ref={fileInputRef}
            onClick={async (event) => {
              event.currentTarget.value = ''; // Reset file input value to ensure `onChange` fires every time
              formik.setFieldValue('file', null); // Set the file and trigger validation
              formik.setFieldTouched('file', false); // Mark the field as touched and trigger validation
            }}
            onChange={async (e) => {
              const file = e.currentTarget.files?.[0];
              await formik.setFieldValue('file', file, true); // Set the file and trigger validation
              await formik.setFieldTouched('file', true, true); // Mark the field as touched and trigger validation
              await formik.validateField('file'); // Explicitly validate the field

              // List name to be set as file uploaded (if unset)
              if (!formik?.values.listName && file) {
                const fileName = file.name;
                formik.setFieldValue(
                  'listName',
                  fileName.substring(0, fileName.lastIndexOf('.')),
                  true
                );
              }
            }}
            accept={validFileExtensions.join(',')}
            className="file-input file-input-bordered file-input-primary !file-input-md w-full"
          />
          <small
            className={classNames(inter400.className, 'text-gray-default pt-2 text-xs')}>
            The list must contain a single text (.txt) or comma-separated value (.csv)
            file with one entry per line. This file may be zipped (.zip) if needed.
          </small>
        </InputContainer>
        <InputContainer
          label="List Name"
          error={formik.errors.listName}
          isTouched={formik.touched.listName}>
          <input
            type="text"
            name="listName"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.listName}
            placeholder="Opt-in April 2024"
            className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
          />
        </InputContainer>

        {shouldValidateIdentity && (
          <InputContainer
            label="Identity"
            error={formik.errors.identity_id}
            isTouched={formik.touched.identity_id}>
            <IdentitySelector
              identities={accountIdentities}
              value={formik.values.identity_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={!!params?.identityId}
              name="identity_id"
              className="w-full"
              currentAccountId={currentAccountId}
              defaultIdentityId={accountDetails?.data?.identity_id}
            />
          </InputContainer>
        )}

        <InputContainer
          label="List Type"
          error={formik.errors.listType}
          isTouched={formik.touched.listType}
          hasMultipleInputs>
          <HorizontalSelectList
            onBlur={() => {
              formik.setFieldTouched('listType', true);
            }}
            onChange={(val) => {
              // Reset optional values
              formik.setFieldValue('split_states', false);
              formik.setFieldValue('use_nat_dnc', false);
              // formik.setFieldValue('use_state_dnc', false);
              formik.setFieldValue('suppress_cell_phones', false);

              // Fix as per: https://github.com/jaredpalmer/formik/issues/2083#issuecomment-884831583
              formik.setFieldValue('listType', val, true);
            }}
            options={
              params?.hideOtherListTypes
                ? listTypeOptions?.filter((listType) => listType.value === params.type)
                : listTypeOptions
            }
            value={formik.values.listType}
          />
        </InputContainer>

        {!!optionalFeatures?.length && (
          <InputContainer label="Optional Features" hasMultipleInputs>
            <div className="flex flex-wrap gap-3">
              {optionalFeatures.map((feature, idx) => {
                return (
                  <div
                    key={idx}
                    className="min-w-[240px] rounded-lg border-[1px] border-gray-200 dark:border-gray-700">
                    <label className="label flex cursor-pointer items-center justify-start gap-3 px-3 py-[10px] text-sm">
                      <input
                        type="checkbox"
                        onBlur={formik.handleBlur}
                        name={feature.key}
                        onChange={formik.handleChange}
                        checked={
                          formik.values[
                            feature.key as keyof UploadContactListFormValues
                          ] === true
                        }
                        value={
                          formik.values[
                            feature.key as keyof UploadContactListFormValues
                          ] as string
                        }
                        className="checkbox-primary checkbox checkbox-sm"
                      />
                      <span className="label-text">{feature.label}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </InputContainer>
        )}

        {hasRateplan && params?.type === 'p2p-sms' && (
          <div className="mt-2">
            <div
              className={classNames(
                inter600.className,
                'mb-3 flex items-center gap-2 text-base'
              )}>
              <SpotlightSvg className="h-5 w-5 text-gray-700" />
              <span>Spotlight</span>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <label className="label flex cursor-pointer items-center justify-start gap-3 text-sm">
                <input
                  type="checkbox"
                  name="uses_rateplan"
                  onChange={formik.handleChange}
                  checked={formik.values.uses_rateplan === true}
                  className="checkbox-primary checkbox checkbox-sm"
                />
                <span className="label-text font-medium">Uses Spotlight</span>
              </label>
              <InfoAlert className="mt-3">
                <span className="text-xs">
                  Spotlight offers discounted messaging rates for fundraising campaigns.
                  By enabling this feature, you confirm this contact list will be used
                  exclusively for fundraising purposes. Jobs using Spotlight contacts must
                  also be designated as fundraising jobs.
                </span>
              </InfoAlert>
            </div>
          </div>
        )}
      </div>
      {/* Form: END */}
    </div>
  );
};

export default P2PUploadStep1UploadFile;
